import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEventDetails } from '../../hooks/useEventDetails';

interface RouteGuardProps {
  children: React.ReactNode;
  eventSlug: string;
}

export const RouteGuard: React.FC<RouteGuardProps> = ({ children, eventSlug }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: eventDetails, isLoading } = useEventDetails(eventSlug);

  useEffect(() => {
    if (isLoading || !eventDetails) return;

    const navOptions = eventDetails.eventOptions;
    const currentPath = location.pathname;

    // Check if current route is active
    const isRouteActive = () => {
      if (currentPath === '/' || currentPath === '/details') {
        return navOptions?.showDetailsNav !== false;
      }
      if (currentPath === '/schedule' || currentPath === '/agenda') {
        return navOptions?.showScheduleNav !== false;
      }
      if (currentPath === '/speakers') {
        return navOptions?.showSpeakersNav === true;
      }
      if (currentPath === '/expo') {
        return navOptions?.showExpoNav === true;
      }
      if (currentPath === '/forms') {
        return navOptions?.showFormsNav === true;
      }
      // Pages and other routes are always active
      return true;
    };

    // Get the first available route
    const getFirstAvailableRoute = () => {
      // Check details
      if (navOptions?.showDetailsNav !== false) {
        return '/details';
      }

      // Check forms (moved between details and schedule)
      if (navOptions?.showFormsNav === true) {
        return '/forms';
      }

      // Check schedule
      if (navOptions?.showScheduleNav !== false) {
        return '/schedule';
      }

      // Check registration (external link, so we can't redirect to it from React Router)
      // Skip registration in the redirect logic since it's external

      // Check expo
      if (navOptions?.showExpoNav === true) {
        return '/expo';
      }

      // Check content pages
      if (eventDetails.contentPages && eventDetails.contentPages.length > 0) {
        return `/pages/${eventDetails.contentPages[0].slug}`;
      }

      // Default fallback
      return '/details';
    };

    // If current route is not active, redirect to first available
    if (!isRouteActive()) {
      const redirectTo = getFirstAvailableRoute();
      if (redirectTo !== currentPath) {
        navigate(redirectTo, { replace: true });
      }
    }
  }, [eventDetails, isLoading, location.pathname, navigate]);

  // Show children while checking (could show a loader here if desired)
  return <>{children}</>;
};